import React from "react";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { themes } from "services/constants";

export const crmContactColors = {
  accent: themes.stepActiveColor || "#00D4AA",
  accentDark: themes.darkGreen || "#188c5a",
  action: themes.primaryButton || "#84DEB8",
  actionHover: "#6cc9a1",
  page: "#F7FBFA",
  section: "#FFFFFF",
  sectionHeader: "#F2FBF8",
  border: "#DDEBE6",
  borderStrong: "#B7E8D7",
  text: "#1F2933",
  muted: "#637381",
};

export const contactFormRootSx = (drawerMode) => ({
  width: "100%",
  maxWidth: drawerMode ? "none" : 1200,
  mx: drawerMode ? 0 : "auto",
  p: drawerMode ? 0 : 2,
  pb: drawerMode ? 1 : 12,
  color: crmContactColors.text,
  "& .MuiInputLabel-root": {
    color: crmContactColors.muted,
    fontSize: 13,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: crmContactColors.accentDark,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: 13,
    transition: "box-shadow 0.18s ease, border-color 0.18s ease",
    "& fieldset": {
      borderColor: "#CFDDD8",
    },
    "&:hover fieldset": {
      borderColor: crmContactColors.borderStrong,
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(0, 212, 170, 0.12)",
    },
    "&.Mui-focused fieldset": {
      borderColor: crmContactColors.accent,
      borderWidth: 1,
    },
  },
  "& .MuiOutlinedInput-input": {
    py: "8.5px",
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: crmContactColors.accentDark,
    fontSize: 18,
  },
  "& .MuiSelect-icon": {
    color: crmContactColors.accentDark,
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: 11,
  },
  "& .MuiButton-root": {
    minHeight: 34,
    borderRadius: "6px",
    fontWeight: 700,
    textTransform: "none",
  },
  "& .MuiButton-outlined": {
    borderColor: crmContactColors.borderStrong,
    color: crmContactColors.accentDark,
    backgroundColor: "#fff",
    "&:hover": {
      borderColor: crmContactColors.accent,
      backgroundColor: "rgba(0, 212, 170, 0.08)",
    },
  },
});

export const itemPaperSx = {
  p: { xs: 1.25, sm: 1.5 },
  mb: 1.5,
  bgcolor: "#FCFEFD",
  border: "1px solid",
  borderColor: crmContactColors.border,
  borderRadius: "8px",
  position: "relative",
};

export const removeItemButtonSx = {
  width: 34,
  height: 34,
  color: "#B42318",
  backgroundColor: "#FFF5F5",
  border: "1px solid #F2C8C8",
  "&:hover": {
    backgroundColor: "#FFECEC",
  },
};

export const footerSx = (drawerMode) => ({
  position: drawerMode ? "sticky" : "fixed",
  bottom: 0,
  left: drawerMode ? "auto" : 0,
  right: drawerMode ? "auto" : 0,
  bgcolor: "#fff",
  borderTop: `1px solid ${crmContactColors.border}`,
  p: { xs: 1, sm: 1.25 },
  mx: drawerMode ? -2 : 0,
  mt: drawerMode ? 2 : 0,
  zIndex: 1000,
  boxShadow: "0 -8px 24px rgba(31, 41, 51, 0.08)",
});

export const primaryActionButtonSx = {
  backgroundColor: crmContactColors.action,
  color: "#10231D",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: crmContactColors.actionHover,
    boxShadow: "0 6px 14px rgba(24, 140, 90, 0.18)",
  },
};

export const Section = ({ title, icon: Icon, children, sx }) => (
  <Card
    elevation={0}
    sx={{
      mb: 2,
      border: `1px solid ${crmContactColors.border}`,
      borderLeft: `4px solid ${crmContactColors.accent}`,
      borderRadius: "8px",
      backgroundColor: crmContactColors.section,
      boxShadow: "0 8px 20px rgba(31, 41, 51, 0.05)",
      overflow: "hidden",
      ...sx,
    }}
  >
    <CardHeader
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              display: "grid",
              placeItems: "center",
              color: crmContactColors.accentDark,
              backgroundColor: "rgba(132, 222, 184, 0.24)",
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
          <Typography variant="subtitle1" component="div" fontWeight={700}>
            {title}
          </Typography>
        </Box>
      }
      sx={{
        py: 1,
        px: 1.5,
        bgcolor: crmContactColors.sectionHeader,
        borderBottom: `1px solid ${crmContactColors.border}`,
      }}
    />
    <CardContent
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        "&:last-child": { pb: { xs: 1.5, sm: 1.75 } },
      }}
    >
      {children}
    </CardContent>
  </Card>
);
